"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Book, Zap, Droplets, Shield, Coins } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function ThothMinorArcanaPage() {
  const suits = [
    {
      name: "Wands",
      element: "Ateş",
      description: "Yaratıcı enerji, tutku ve ruhsal güç",
      icon: <Zap className="h-6 w-6 text-white" />,
      color: "from-red-500 to-orange-700",
      path: "wands",
      keywords: ["Yaratıcılık", "Enerji", "Girişim", "Ruhsal güç"]
    },
    {
      name: "Cups",
      element: "Su",
      description: "Duygular, aşk, sezgi ve ruhsal bağlantı",
      icon: <Droplets className="h-6 w-6 text-white" />,
      color: "from-blue-500 to-indigo-700",
      path: "cups",
      keywords: ["Aşk", "Duygular", "Sezgi", "Empati"]
    },
    {
      name: "Swords",
      element: "Hava",
      description: "Zihin, düşünce, çatışma ve iletişim",
      icon: <Shield className="h-6 w-6 text-white" />,
      color: "from-gray-500 to-slate-700",
      path: "swords",
      keywords: ["Zihin", "Mantık", "Çatışma", "Adalet"]
    },
    {
      name: "Disks",
      element: "Toprak",
      description: "Maddi dünya, para, sağlık ve pratik konular",
      icon: <Coins className="h-6 w-6 text-white" />,
      color: "from-green-500 to-emerald-700",
      path: "disks",
      keywords: ["Para", "Sağlık", "İş", "Maddi başarı"]
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-indigo-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-indigo-500">/</span>
            <span className="text-sm">Thoth</span>
            <span className="text-indigo-500">/</span>
            <span className="text-sm">Minör Arkana</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/thoth"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Thoth Tarot
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Minör Arkana</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-900/20 to-purple-900/20 rounded-xl p-8 mb-8 border border-indigo-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Book className="h-10 w-10 text-indigo-400" />
            Thoth Minör Arkana
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            56 karttan oluşan Minör Arkana, Crowley'nin astrolojik ve Kabalistik sistemine göre yeniden tasarlanmış.
            Her kart planetary ve zodiac etkileri ile güçlendirilmiş ve detaylı sembolik anlatım sunuyor.
          </p>
        </div>

        {/* Thoth Court Cards */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Thoth Court Kartları Sistemi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Yeni İsimlendirme</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Princess</strong> - Genç dişil enerji (Page yerine)</li>
                <li>• <strong>Prince</strong> - Genç eril enerji (Knight yerine)</li>
                <li>• <strong>Queen</strong> - Olgun dişil enerji</li>
                <li>• <strong>Knight</strong> - Olgun eril enerji (King yerine)</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Elemental Yaklaşım</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Princess: Element'in Toprak aspekti</li>
                <li>• Prince: Element'in Hava aspekti</li>
                <li>• Queen: Element'in Su aspekti</li>
                <li>• Knight: Element'in Ateş aspekti</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Four Suits */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Dört Suit</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {suits.map((suit, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-indigo-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${suit.color} rounded-lg flex items-center justify-center`}>
                    {suit.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100">{suit.name}</h3>
                    <p className="text-sm text-slate-400">{suit.element} elementi</p>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">{suit.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {suit.keywords.map((keyword, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                      {keyword}
                    </span>
                  ))}
                </div>
                <Link
                  href={`/about/tarot/systems/thoth/minor-arcana/${suit.path}`}
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${suit.color} text-white rounded-lg transition-colors text-sm hover:shadow-lg`}
                >
                  <Book className="h-4 w-4 mr-2" />
                  {suit.name} Kartlarını Gör
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Astrological Correspondences */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Astrolojik Correspondence'lar</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Pip Kartları (Ace-10)</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• <strong>Ace:</strong> Element'in saf gücü</li>
                  <li>• <strong>2-10:</strong> Decanlar (10°'lik bölümler)</li>
                  <li>• Her kart belirli planetary etkiler taşır</li>
                  <li>• Zodiac işaretleri ile bağlantılı</li>
                  <li>• Tree of Life'da Sephirot'lara karşılık gelir</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Crowley'nin Özel Adlandırmaları</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• Her pip kartının özel ismi var</li>
                  <li>• Örnek: "Peace" (Two of Swords)</li>
                  <li>• "Completion" (Ten of Disks)</li>
                  <li>• "Virtue" (Three of Wands)</li>
                  <li>• Astrolojik ve Kabalistik anlamları birleştirir</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Differences from RWS */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Rider-Waite'dan Farkları</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Thoth Yaklaşımı</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Pentacles → Disks (daha materyalist)</li>
                <li>• Court kartlarında yeni hiyerarşi</li>
                <li>• Her kartın astrolojik correspondence'ı</li>
                <li>• Daha soyut ve sembolik sanat</li>
                <li>• Golden Dawn'un geliştirilmiş sistemi</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Görsel Farklılıklar</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Lady Frieda Harris'in sanatsal stili</li>
                <li>• Art Deco etkileri</li>
                <li>• Daha dinamik ve enerjik kompozisyonlar</li>
                <li>• Mısır mitolojisi referansları</li>
                <li>• Geometrik ve matematiksel semboller</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları Thoth Tarot'un geleneksel yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/thoth/major-arcana"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Majör Arkana
          </Link>

          <Link
            href="/about/tarot/systems/thoth"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            Thoth Tarot
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}